import BottomSheet from '@components/BottomSheet'
import Button from '@components/Button'
import Prompt from '@components/Prompt'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import TokenAvatar from '@components/TokenAvatar'
import { useDevMode } from '@plug-core/state'
import CustomTokenForm from '@modules/Tokens/Components/CustomTokenForm'
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { HiChevronRight } from 'react-icons/hi2'
import { useNavigate, useParams } from 'react-router-dom'

const NetworkForm = () => {
  const [kindInEdit, setKindInEdit] = useState<string>()
  const [newHostUrl, setNewHostUrl] = useState<string>()
  const [name, setName] = useState<string>()
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false)

  const { networkId } = useParams<{ networkId: string }>()

  const { t } = useTranslation()
  const {
    addNetwork,
    networks,
    networkTokens,
    activeNetwork,
    addTokenToNetwork,
    updateNetwork,
    devEnabled,
    setActiveNetwork,
  } = useDevMode()

  const navigate = useNavigate()

  useEffect(() => {
    if (!networkId) return

    setName(networks[networkId]?.name)
    setNewHostUrl(networks[networkId]?.hostUrl)
  }, [networks, networkId])

  const saveNetwork = () => {
    if (networkId) {
      updateNetwork(networkId, name!, newHostUrl!)
    } else {
      addNetwork(name!, newHostUrl!)
    }
    navigate(-1)
  }

  const renderPropertyRow = (kind: string, value?: string, onClick?: () => void) => (
    <ListItem onClick={onClick}>
      <ListItemText primary={t(`newNetwork.${kind}.title`)} secondary={value || 'Not set'} />
      <ListItemSecondaryAction>
        <HiChevronRight />
      </ListItemSecondaryAction>
    </ListItem>
  )

  const renderTestTokens = () => {
    if (!networkId) return

    return (
      <Stack mt={2}>
        <Typography variant="h6">Network Test Tokens</Typography>
        <List>
          {(networkTokens[networkId] || []).map((networkToken) => (
            <ListItem>
              <ListItemAvatar>
                <TokenAvatar logo={networkToken.logo} />
              </ListItemAvatar>
              <ListItemText primary={networkToken.name} secondary={networkToken.symbol} />
            </ListItem>
          ))}
        </List>
        <Button
          variant="link"
          onClick={() => setBottomSheetVisible(true)}
          disabled={networkId !== activeNetwork}
          fullWidth
        >
          {networkId !== activeNetwork
            ? 'Network should be activated to add tokens'
            : 'Add test token'}
        </Button>
        <BottomSheet
          visible={bottomSheetVisible}
          onBackdropClick={() => setBottomSheetVisible(false)}
        >
          <CustomTokenForm
            hideForm={() => setBottomSheetVisible(false)}
            customSaveAction={(tokenDetails) => addTokenToNetwork(networkId, tokenDetails)}
          />
        </BottomSheet>
      </Stack>
    )
  }

  return (
    <Stack mx={2}>
      <List>
        {renderPropertyRow('name', name, () => setKindInEdit('name'))}
        {renderPropertyRow('host', newHostUrl, () => setKindInEdit('host'))}
      </List>
      <Prompt show={!!kindInEdit} close={() => setKindInEdit(undefined)}>
        <Stack alignItems="center" spacing={2}>
          <Typography variant="h6">{t(`newNetwork.${kindInEdit}.title`)}</Typography>
          <Stack width="100%" spacing={1}>
            <RainbowTextField
              placeholder={t(`newNetwork.${kindInEdit}.placeholder`)}
              value={kindInEdit === 'name' ? name : newHostUrl}
              onChange={(event) =>
                kindInEdit === 'name'
                  ? setName(event.target.value)
                  : setNewHostUrl(event.target.value)
              }
              autoFocus
              fullWidth
            />
            <Button fullWidth onClick={() => setKindInEdit(undefined)} variant="rainbow">
              {t('common.save')}
            </Button>
          </Stack>
        </Stack>
      </Prompt>
      <Button
        onClick={() => saveNetwork()}
        variant="rainbow"
        disabled={!name || !newHostUrl}
        fullWidth
      >
        {t(networkId ? 'newNetwork.update' : 'newNetwork.create')}
      </Button>
      {renderTestTokens()}
    </Stack>
  )
}

export default NetworkForm
